import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import ToggleSearchBar from 'components/ToggleSearchBar/ToggleSearchBar';
import CreateEmailTemplate from 'components/CreateEmailTemplate/CreateEmailTemplate';
import EditEmailTemplate from 'components/EditEmailTemplate/EditEmailTemplate';
import EmailTemplatePreview from 'components/EmailTemplatePreview/EmailTemplatePreview';
import SendTestEmailModal from 'components/SendTestEmailModal/SendTestEmailModal';
import Spinner from 'components/Spinner/Spinner';

import {
    actions,
} from 'coupon-common/src/modules/admin/emailManager';

import styles from './EmailTemplateManager.module.scss';

const EmailTemplateManager = ({
    dispatch,
    templates,
}) => {
    const [mode, setMode] = useState('create');

    const [selectedTemplate, setSelectedTemplate] =
        useState(null);

    const [showPreview, setShowPreview] =
        useState(false);

    const [previewValues, setPreviewValues] =
        useState(null);

    const [showTestEmailModal, setShowTestEmailModal] =
        useState(false);

    const [testEmailValues, setTestEmailValues] =
        useState({
            subject: '',
            body: '',
        });

    const [fetching, setFetching] =
        useState(false);

    useEffect(() => {
        setFetching(true);

        dispatch(
            actions.getEmailTemplates(result => {
                setFetching(false);

                if (!result?.success) {
                    console.error(
                        'Failed to fetch email templates:',
                        result?.error
                    );
                }
            })
        );
    }, [dispatch]);

    const baseTemplate = templates.find(
        template =>
            template.name ===
            'BASE_EMAIL_TEMPLATE'
    );

    useEffect(() => {
        if (
            mode === 'edit' &&
            !selectedTemplate &&
            templates.length > 0
        ) {
            setSelectedTemplate(templates[0]);
        }
    }, [
        mode,
        templates,
        selectedTemplate,
    ]);

    const handleModeChange = newMode => {
        setMode(newMode);
        setShowPreview(false);
        setPreviewValues(null);
        setShowTestEmailModal(false);

        if (newMode === 'create') {
            setSelectedTemplate(null);
        }

        if (
            newMode === 'edit' &&
            templates.length > 0
        ) {
            setSelectedTemplate(templates[0]);
        }
    };

    const handleTemplateChange = event => {
        const templateId = Number(
            event.target.value
        );

        const template = templates.find(
            item => item.id === templateId
        );

        setSelectedTemplate(template);
        setShowPreview(false);
        setPreviewValues(null);
        setShowTestEmailModal(false);
    };

    const buildPreviewHtml = values => {
        let html = values?.body || '';

        if (
            values?.useBaseTemplate &&
            baseTemplate
        ) {
            html = baseTemplate.body.replace(
                '{content}',
                html
            );
        }

        return html;
    };

    const handlePreview = values => {
        setPreviewValues(values);
        setShowPreview(true);
    };

    const handleSendTestEmail = values => {
        setTestEmailValues({
            subject: values.subject || '',
            body: buildPreviewHtml(values),
        });

        setShowTestEmailModal(true);
    };

    const handleCloseTestEmailModal = () => {
        setShowTestEmailModal(false);

        setTestEmailValues({
            subject: '',
            body: '',
        });
    };

    const handleCreated = () => {
        setMode('edit');
        setShowPreview(false);
        setPreviewValues(null);
    };

    const handleUpdated = () => {
        setShowPreview(false);
        setPreviewValues(null);
    };

    const createInitialValues = {
        name: '',
        subject: '',
        body: '',
        enabled: true,
        transactional: false,
        useBaseTemplate: true,
    };

    const editInitialValues =
        selectedTemplate
            ? {
                name:
                    selectedTemplate.name || '',
                subject:
                    selectedTemplate.subject || '',
                body:
                    selectedTemplate.body || '',
                enabled:
                    selectedTemplate.enabled,
                transactional:
                    selectedTemplate.transactional,
                useBaseTemplate:
                    selectedTemplate.name !==
                    'BASE_EMAIL_TEMPLATE',
            }
            : {
                name: '',
                subject: '',
                body: '',
                enabled: true,
                transactional: false,
                useBaseTemplate: true,
            };

    return (
        <div className={styles.container}>
            <ToggleSearchBar
                options={[
                    {
                        label: 'Create New Template',
                        value: 'create',
                    },
                    {
                        label: 'Edit Templates',
                        value: 'edit',
                    },
                ]}
                activeValue={mode}
                onChange={handleModeChange}
            />

            {fetching && (
                <Spinner show={fetching} />
            )}

            {!fetching &&
                mode === 'create' && (
                    <CreateEmailTemplate
                        initialValues={
                            createInitialValues
                        }
                        onPreview={handlePreview}
                        onSendTestEmail={
                            handleSendTestEmail
                        }
                        onCreated={handleCreated}
                        styles={styles}
                    />
                )}

            {!fetching &&
                mode === 'edit' && (
                    <EditEmailTemplate
                        templates={templates}
                        selectedTemplate={
                            selectedTemplate
                        }
                        initialValues={
                            editInitialValues
                        }
                        onTemplateChange={
                            handleTemplateChange
                        }
                        onPreview={handlePreview}
                        onSendTestEmail={
                            handleSendTestEmail
                        }
                        onUpdated={handleUpdated}
                        styles={styles}
                    />
                )}

            {showPreview &&
                previewValues && (
                    <EmailTemplatePreview
                        subject={
                            previewValues.subject
                        }
                        html={buildPreviewHtml(
                            previewValues
                        )}
                        onHide={() =>
                            setShowPreview(false)
                        }
                    />
                )}

            <SendTestEmailModal
                isOpen={showTestEmailModal}
                onRequestClose={
                    handleCloseTestEmailModal
                }
                subject={
                    testEmailValues.subject
                }
                body={
                    testEmailValues.body
                }
            />
        </div>
    );
};

EmailTemplateManager.propTypes = {
    dispatch: PropTypes.func.isRequired,
    templates: PropTypes.array,
};

EmailTemplateManager.defaultProps = {
    templates: [],
};

const mapStateToProps = state => ({
    templates:
        state.admin.emailManager?.list?.data || [],
});

export default connect(mapStateToProps)(
    EmailTemplateManager
);